<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

import { useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();

const limitsState = ref({
  isLoading: true,
  data: {
    plan_available: 0,
    meta_daily_limit: 0,
    sends_today: 0,
    remaining_today: 0
  }
});

const selectedInboxId = ref(null);

const inboxes = computed(() => store.getters['inboxes/getWhatsAppInboxes'] || []);

const inboxOptions = computed(() =>
  inboxes.value.map(item => ({
    value: item.id,
    label: item.name,
  }))
);

watch(
  () => inboxes.value,
  (newInboxes) => {
    if (newInboxes.length > 0 && !selectedInboxId.value) {
      selectedInboxId.value = newInboxes[0].id;
    }
  },
  { immediate: true }
);

const fetchLimits = async () => {
  if (!selectedInboxId.value) return;
  
  limitsState.value.isLoading = true;
  try {
    const data = await store.dispatch('campaigns/fetchLimits', selectedInboxId.value);
    limitsState.value.data = data;
  } catch (e) {
    // ignore
  }
  limitsState.value.isLoading = false;
};

watch(
  selectedInboxId,
  () => {
    fetchLimits();
  },
  { immediate: true }
);

const planAvailable = computed(() => limitsState.value.data.plan_available || 0);
const isUnlimited = computed(() => limitsState.value.data.meta_daily_limit === -1);
const remainingToday = computed(() => isUnlimited.value ? 'Ilimitado' : limitsState.value.data.remaining_today || 0);

const dailyPercentage = computed(() => {
  if (isUnlimited.value) return 0;
  const metaLimit = limitsState.value.data.meta_daily_limit || 0;
  if (metaLimit === 0) return 100;
  return Math.min((limitsState.value.data.sends_today / metaLimit) * 100, 100);
});

const progressBarColor = computed(() => {
  if (dailyPercentage.value > 90) return 'bg-n-ruby-9';
  if (dailyPercentage.value > 75) return 'bg-n-amber-9';
  return 'bg-n-teal-9';
});

const handleBuyMore = () => {
  const accountId = store.getters.getCurrentAccountId;
  router.push(`/app/accounts/${accountId}/settings/billing`);
};
</script>

<template>
  <div class="relative overflow-hidden border rounded-xl bg-gradient-to-br from-n-slate-1 to-n-slate-3 border-n-slate-4 shadow-sm mb-6">
    <div class="absolute top-0 right-0 w-64 h-64 bg-n-teal-4/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
    <div class="absolute bottom-0 left-0 w-48 h-48 bg-n-blue-4/20 rounded-full blur-2xl translate-y-1/3 -translate-x-1/4"></div>
    
    <div class="relative p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      
      <div v-if="limitsState.isLoading" class="flex items-center gap-3 text-n-slate-11 w-full justify-center py-4">
        <span class="i-lucide-loader-2 animate-spin w-5 h-5" />
        <span class="text-sm font-medium">Cargando límites en tiempo real...</span>
      </div>

      <template v-else>
        <div class="flex-1 flex flex-col gap-2">
          <div class="flex flex-col sm:flex-row sm:items-center gap-4">
            <div class="flex items-center gap-2">
              <span class="i-logos-whatsapp-icon w-5 h-5"></span>
              <h3 class="text-lg font-bold text-n-slate-12 m-0">Límites de Envíos WhatsApp</h3>
              <span class="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-n-teal-3 text-n-teal-11 rounded-full flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-n-teal-9 animate-pulse"></span>
                En Vivo
              </span>
            </div>
            
            <div class="w-full sm:w-64 z-10">
              <ComboBox
                id="banner-inbox"
                v-model="selectedInboxId"
                :options="inboxOptions"
                placeholder="Seleccione una bandeja"
                class="[&>div>button]:bg-white [&>div>button]:dark:bg-n-slate-2"
              />
            </div>
          </div>
          
          <p class="text-sm text-n-slate-11 max-w-lg m-0 mt-1">
            Monitorea el consumo de mensajes de tu plan y los límites diarios impuestos por Meta.
          </p>
        </div>

        <div class="flex flex-col sm:flex-row items-stretch gap-4 w-full md:w-auto">
          <div class="flex flex-col p-4 rounded-xl bg-white dark:bg-n-slate-2 border border-n-slate-3 shadow-sm flex-1">
            <span class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider mb-1">Cupo del Plan</span>
            <div class="flex items-baseline gap-1">
              <span class="text-2xl font-black text-n-slate-12 leading-none">{{ planAvailable.toLocaleString() }}</span>
              <span class="text-xs text-n-slate-11">msgs libres</span>
            </div>
          </div>

          <div class="flex flex-col p-4 rounded-xl bg-white dark:bg-n-slate-2 border border-n-slate-3 shadow-sm flex-1">
            <div class="flex justify-between items-center mb-1">
              <span class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider">Límite Diario Meta</span>
              <span class="text-xs font-bold text-n-slate-12">{{ isUnlimited ? '∞' : `${dailyPercentage.toFixed(0)}%` }}</span>
            </div>
            
            <div class="flex items-baseline gap-1 mb-2">
              <span class="text-2xl font-black text-n-slate-12 leading-none">{{ remainingToday.toLocaleString() }}</span>
              <span class="text-xs text-n-slate-11">restantes</span>
            </div>

            <div v-if="!isUnlimited" class="w-full h-1.5 bg-n-slate-3 rounded-full overflow-hidden">
              <div 
                class="h-full rounded-full transition-all duration-1000 ease-out"
                :class="progressBarColor"
                :style="{ width: `${dailyPercentage}%` }"
              ></div>
            </div>
          </div>
        </div>

        <div class="flex-shrink-0 w-full md:w-auto mt-2 md:mt-0">
          <Button
            label="Adquirir más mensajes"
            icon="i-lucide-shopping-cart"
            color="blue"
            class="shadow-md hover:shadow-lg transition-shadow w-full md:w-auto justify-center"
            @click="handleBuyMore"
          />
        </div>
      </template>

    </div>
  </div>
</template>
